// src/lib/constants/invoice.ts
import { SelectOption } from '@/components/common/SelectDropdown';

// CGST_SGST: intra-state supply (same state), rate split equally between CGST and SGST.
// IGST: inter-state supply (different state), full rate charged as IGST.
export type GstType = 'CGST_SGST' | 'IGST';

export const GST_SLABS = [0, 5, 12, 18, 28];

// Item taxValue is stored as `${GstType}_${rate}`, e.g. 'CGST_SGST_18' or 'IGST_18'
export const toTaxValue = (gstType: GstType, rate: number) => `${gstType}_${rate}`;

export const CGST_SGST_TAX_OPTIONS = GST_SLABS.map((rate) => ({
  label: `CGST+SGST [${rate}%]`,
  value: toTaxValue('CGST_SGST', rate),
  description: rate === 0 ? 'Zero rated' : `CGST ${rate / 2}% + SGST ${rate / 2}%`,
}));

export const IGST_TAX_OPTIONS = GST_SLABS.map((rate) => ({
  label: `IGST [${rate}%]`,
  value: toTaxValue('IGST', rate),
  description: rate === 0 ? 'Zero rated' : `IGST ${rate}%`,
}));

export const TAX_META = {
  out_of_scope: {
    label: 'Out of Scope Supply',
    note: 'This is an out-of-scope supply under GST. No tax has been charged.',
    taxable: false,
  },
  non_gst_supply: {
    label: 'Non-GST Supply',
    note: 'This is a non-GST supply. GST is not applicable.',
    taxable: false,
  },
};

export const currencySymbols: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'Fr',
  CNY: '¥',
};

export const gstStateList = {
  'Andhra Pradesh': { code: '37', abbreviation: 'AP' },
  'Arunachal Pradesh': { code: '12', abbreviation: 'AR' },
  Assam: { code: '18', abbreviation: 'AS' },
  Bihar: { code: '10', abbreviation: 'BR' },
  Chhattisgarh: { code: '22', abbreviation: 'CG' },
  Goa: { code: '30', abbreviation: 'GA' },
  Gujarat: { code: '24', abbreviation: 'GJ' },
  Haryana: { code: '06', abbreviation: 'HR' },
  'Himachal Pradesh': { code: '02', abbreviation: 'HP' },
  Jharkhand: { code: '20', abbreviation: 'JH' },
  Karnataka: { code: '29', abbreviation: 'KA' },
  Kerala: { code: '32', abbreviation: 'KL' },
  'Madhya Pradesh': { code: '23', abbreviation: 'MP' },
  Maharashtra: { code: '27', abbreviation: 'MH' },
  Manipur: { code: '14', abbreviation: 'MN' },
  Meghalaya: { code: '17', abbreviation: 'ML' },
  Mizoram: { code: '15', abbreviation: 'MZ' },
  Nagaland: { code: '13', abbreviation: 'NL' },
  Odisha: { code: '21', abbreviation: 'OD' },
  Punjab: { code: '03', abbreviation: 'PB' },
  Rajasthan: { code: '08', abbreviation: 'RJ' },
  Sikkim: { code: '11', abbreviation: 'SK' },
  'Tamil Nadu': { code: '33', abbreviation: 'TN' },
  Telangana: { code: '36', abbreviation: 'TS' },
  Tripura: { code: '16', abbreviation: 'TR' },
  'Uttar Pradesh': { code: '09', abbreviation: 'UP' },
  Uttarakhand: { code: '05', abbreviation: 'UK' },
  'West Bengal': { code: '19', abbreviation: 'WB' },
  Delhi: { code: '07', abbreviation: 'DL' },
  'Jammu and Kashmir': { code: '01', abbreviation: 'JK' },
  Ladakh: { code: '38', abbreviation: 'LA' },
  Chandigarh: { code: '04', abbreviation: 'CH' },
  'Andaman and Nicobar Islands': { code: '35', abbreviation: 'AN' },
  'Dadra and Nagar Haveli and Daman and Diu': { code: '26', abbreviation: 'DNHDD' },
  Lakshadweep: { code: '31', abbreviation: 'LD' },
  Puducherry: { code: '34', abbreviation: 'PY' },
};

export const invoiceStatusOptions: SelectOption[] = [
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
];

export const invoiceSortOptions: SelectOption[] = [
  { value: 'invoiceDate-desc', label: 'Invoice Date (Newest)' },
  { value: 'invoiceDate-asc', label: 'Invoice Date (Oldest)' },
  { value: 'dueDate-desc', label: 'Due Date (Newest)' },
  { value: 'dueDate-asc', label: 'Due Date (Oldest)' },
  { value: 'invoiceNumber-asc', label: 'Invoice Number (A-Z)' },
  { value: 'invoiceNumber-desc', label: 'Invoice Number (Z-A)' },
  { value: 'totalAmount-desc', label: 'Amount (High to Low)' },
  { value: 'totalAmount-asc', label: 'Amount (Low to High)' },
  { value: 'customer-asc', label: 'Customer (A-Z)' },
  { value: 'customer-desc', label: 'Customer (Z-A)' },
];

export const invoiceTermsOptions: SelectOption[] = [
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: 'Net 10', label: 'Net 10' },
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'EOM', label: 'End of Month (EOM)' },
  { value: 'COD', label: 'Cash on Delivery (COD)' },
  { value: 'Advance Payment', label: 'Advance Payment' },
  { value: 'Custom', label: 'Custom' },
];

export const invoiceTitleOptions: SelectOption[] = [
  { value: 'tax_invoice', label: 'TAX Invoice' },
  { value: 'proforma_invoice', label: 'Proforma Invoice' },
  { value: 'overdue_invoice', label: 'Overdue Invoice' },
  { value: 'consolidated_invoice', label: 'Consolidated Invoice' },
  { value: 'retainer_invoice', label: 'Retainer Invoice' },
  { value: 'interim_invoice', label: 'Interim Invoice' },
  { value: 'timesheet_invoice', label: 'Timesheet Invoice' },
  { value: 'final_invoice', label: 'Final Invoice' },
];

export const getInvoiceTitleLabel = (value?: string): string => {
  if (!value) return 'TAX INVOICE';
  const normalizedValue = String(value).trim().toLowerCase();

  const option = invoiceTitleOptions.find((opt) => opt.value === normalizedValue);
  if (option) return option.label.toUpperCase();

  // Backward-compatible support if API sends label-like values.
  if (normalizedValue.includes('proforma')) return 'PROFORMA INVOICE';
  if (normalizedValue.includes('overdue')) return 'OVERDUE INVOICE';
  if (normalizedValue.includes('consolidated')) return 'CONSOLIDATED INVOICE';
  if (normalizedValue.includes('retainer')) return 'RETAINER INVOICE';
  if (normalizedValue.includes('interim')) return 'INTERIM INVOICE';
  if (normalizedValue.includes('timesheet')) return 'TIMESHEET INVOICE';
  if (normalizedValue.includes('final')) return 'FINAL INVOICE';

  return 'TAX INVOICE';
};

// Both GST groups are always offered; the one matching the place of supply comes first
export const getTaxOptions = (recommendedType: GstType) => {
  const cgstSgstGroup = {
    group: 'CGST + SGST (Same State)',
    taxable: true,
    recommended: recommendedType === 'CGST_SGST',
    items: CGST_SGST_TAX_OPTIONS,
  };
  const igstGroup = {
    group: 'IGST (Other State)',
    taxable: true,
    recommended: recommendedType === 'IGST',
    items: IGST_TAX_OPTIONS,
  };

  return [
    {
      group: 'Non-Taxable',
      taxable: false,
      recommended: false,
      items: [
        {
          label: 'Out of Scope',
          description: "Supplies on which you don't charge any GST or include them in returns.",
          value: 'out_of_scope',
        },
        {
          label: 'Non-GST Supply',
          description:
            'Supplies which do not come under GST such as petroleum products and liquor.',
          value: 'non_gst_supply',
        },
      ],
    },
    ...(recommendedType === 'IGST' ? [igstGroup, cgstSgstGroup] : [cgstSgstGroup, igstGroup]),
  ];
};
